import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Plus } from "lucide-react";
import { getToken } from "../../utils/auth";
import { parseBackendDate } from "../../utils/datetime";
import Modal, { ModalCloseButton } from "../ui/Modal";

const formatDt = (value) => {
  const date = parseBackendDate(value);
  if (!date) return "—";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const emptyForm = {
  activity_code: "",
  name: "",
  purpose: "",
  lawful_basis: "explicit_consent",
  retention_period: "",
  sensitive_data: false,
  is_active: true,
  sort_order: 100,
  data_categories: "",
  data_subject_categories: "",
  recipients: "",
  security_measures: "",
};

const splitCsv = (value) =>
  String(value || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

/**
 * Admin RoPA register panel — Record of Processing Activities.
 */
const ProcessingActivitiesPanel = ({ apiBaseUrl }) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [activeOnly, setActiveOnly] = useState("true");
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const headers = useCallback(() => {
    const token = getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: "0",
        limit: "200",
        sort_by: "sort_order",
        sort_dir: "asc",
      });
      if (activeOnly === "true") params.set("is_active", "true");
      if (activeOnly === "false") params.set("is_active", "false");
      if (search.trim()) params.set("search", search.trim());
      const res = await axios.get(
        `${apiBaseUrl}/compliance/processing-activities?${params}`,
        { headers: headers() }
      );
      setItems(res.data?.items || []);
      setTotal(res.data?.total ?? 0);
    } catch (e) {
      console.error("RoPA fetch failed", e);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, activeOnly, search, headers]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      activity_code: row.activity_code || "",
      name: row.name || "",
      purpose: row.purpose || "",
      lawful_basis: row.lawful_basis || "",
      retention_period: row.retention_period || "",
      sensitive_data: !!row.sensitive_data,
      is_active: row.is_active !== false,
      sort_order: row.sort_order ?? 100,
      data_categories: (row.data_categories || []).join(", "),
      data_subject_categories: (row.data_subject_categories || []).join(", "),
      recipients: (row.recipients || []).join(", "),
      security_measures: row.security_measures || "",
    });
    setShowForm(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        purpose: form.purpose.trim(),
        lawful_basis: form.lawful_basis.trim(),
        retention_period: form.retention_period.trim(),
        sensitive_data: !!form.sensitive_data,
        is_active: !!form.is_active,
        sort_order: Number(form.sort_order) || 100,
        data_categories: splitCsv(form.data_categories),
        data_subject_categories: splitCsv(form.data_subject_categories),
        recipients: splitCsv(form.recipients),
        security_measures: form.security_measures.trim() || null,
      };
      if (editingId) {
        await axios.patch(
          `${apiBaseUrl}/compliance/processing-activities/${editingId}`,
          payload,
          { headers: headers() }
        );
      } else {
        await axios.post(
          `${apiBaseUrl}/compliance/processing-activities`,
          {
            ...payload,
            activity_code: form.activity_code.trim(),
          },
          { headers: headers() }
        );
      }
      setShowForm(false);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to save processing activity.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <p className="text-sm text-gray-600 mb-4">
        Processing Activities. Maintain categories of personal-data processing activities for accountability reviews.
      </p>
      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name or code…"
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
        />
        <select
          value={activeOnly}
          onChange={(e) => setActiveOnly(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
        >
          <option value="true">Active only</option>
          <option value="false">Inactive only</option>
          <option value="">All</option>
        </select>
        <button
          type="button"
          onClick={openCreate}
          className="ms-auto flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm hover:bg-teal-700"
        >
          <Plus size={16} />
          Add activity
        </button>
      </div>

      {loading ? (
        <p className="text-gray-500">Loading…</p>
      ) : (
        <div className="overflow-x-auto max-h-[520px] overflow-y-auto border border-gray-200 rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-start">Code</th>
                <th className="px-3 py-2 text-start">Name</th>
                <th className="px-3 py-2 text-start">Lawful basis</th>
                <th className="px-3 py-2 text-start">Sensitive</th>
                <th className="px-3 py-2 text-start">Retention</th>
                <th className="px-3 py-2 text-start">Updated</th>
                <th className="px-3 py-2 text-start"> </th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-3 py-2 font-mono text-xs">{row.activity_code}</td>
                  <td className="px-3 py-2">{row.name}</td>
                  <td className="px-3 py-2">{row.lawful_basis}</td>
                  <td className="px-3 py-2">{row.sensitive_data ? "Yes" : "No"}</td>
                  <td className="px-3 py-2 max-w-[220px] truncate" title={row.retention_period}>
                    {row.retention_period}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">{formatDt(row.updated_at)}</td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      className="text-teal-700 text-xs font-medium hover:underline"
                      onClick={() => openEdit(row)}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
              {!items.length && (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-center text-gray-500">
                    No processing activities yet. Run the RoPA migration seed or add one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-gray-500 mt-2">{total} activities</p>

      <Modal open={showForm} onClose={() => setShowForm(false)}>
        <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold text-gray-900">
              {editingId ? "Edit processing activity" : "New processing activity"}
            </h3>
            <ModalCloseButton onClose={() => setShowForm(false)} />
          </div>
          <div className="grid gap-3">
            {!editingId && (
              <label className="text-sm">
                Code
                <input
                  className="mt-1 w-full border rounded-lg px-3 py-2"
                  value={form.activity_code}
                  onChange={(e) => setForm({ ...form, activity_code: e.target.value })}
                />
              </label>
            )}
            <label className="text-sm">
              Name
              <input
                className="mt-1 w-full border rounded-lg px-3 py-2"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </label>
            <label className="text-sm">
              Purpose
              <textarea
                className="mt-1 w-full border rounded-lg px-3 py-2"
                rows={3}
                value={form.purpose}
                onChange={(e) => setForm({ ...form, purpose: e.target.value })}
              />
            </label>
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="text-sm">
                Lawful basis
                <input
                  className="mt-1 w-full border rounded-lg px-3 py-2"
                  value={form.lawful_basis}
                  onChange={(e) => setForm({ ...form, lawful_basis: e.target.value })}
                />
              </label>
              <label className="text-sm">
                Retention period
                <input
                  className="mt-1 w-full border rounded-lg px-3 py-2"
                  value={form.retention_period}
                  onChange={(e) => setForm({ ...form, retention_period: e.target.value })}
                />
              </label>
            </div>
            <label className="text-sm">
              Data categories (comma-separated)
              <input
                className="mt-1 w-full border rounded-lg px-3 py-2"
                value={form.data_categories}
                onChange={(e) => setForm({ ...form, data_categories: e.target.value })}
              />
            </label>
            <label className="text-sm">
              Data subject categories (comma-separated)
              <input
                className="mt-1 w-full border rounded-lg px-3 py-2"
                value={form.data_subject_categories}
                onChange={(e) =>
                  setForm({ ...form, data_subject_categories: e.target.value })
                }
              />
            </label>
            <label className="text-sm">
              Recipients (comma-separated)
              <input
                className="mt-1 w-full border rounded-lg px-3 py-2"
                value={form.recipients}
                onChange={(e) => setForm({ ...form, recipients: e.target.value })}
              />
            </label>
            <label className="text-sm">
              Security measures
              <textarea
                className="mt-1 w-full border rounded-lg px-3 py-2"
                rows={2}
                value={form.security_measures}
                onChange={(e) => setForm({ ...form, security_measures: e.target.value })}
              />
            </label>
            <div className="flex flex-wrap gap-4 text-sm">
              <label className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.sensitive_data}
                  onChange={(e) => setForm({ ...form, sensitive_data: e.target.checked })}
                />
                Sensitive data
              </label>
              <label className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                />
                Active
              </label>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <button
              type="button"
              className="px-4 py-2 border rounded-lg text-sm"
              onClick={() => setShowForm(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={saving}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm disabled:opacity-50"
              onClick={save}
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ProcessingActivitiesPanel;
